import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { caseService, interventionService, estudianteService } from "@/services/api";
import { mapDbCaseToFrontend } from "@/services/mapping";

const INTERVENCION_TIPOS = [
  { value: "entrevista_estudiante", label: "Entrevista a estudiante", icon: "ri-user-voice-line" },
  { value: "entrevista_apoderado", label: "Entrevista a apoderado", icon: "ri-parent-line" },
  { value: "entrevista_docente", label: "Entrevista a docente", icon: "ri-presentation-line" },
  { value: "observacion_conducta", label: "Observación de conducta", icon: "ri-eye-line" },
  { value: "derivacion_interna", label: "Derivación interna", icon: "ri-arrow-right-circle-line" },
  { value: "derivacion_externa", label: "Derivación externa", icon: "ri-external-link-line" },
  { value: "seguimiento_psicologico", label: "Seguimiento psicológico", icon: "ri-brain-line" },
  { value: "seguimiento_orientacion", label: "Seguimiento orientación", icon: "ri-compass-line" },
  { value: "medida_formativa", label: "Medida formativa", icon: "ri-book-read-line" },
  { value: "medida_disciplinaria", label: "Medida disciplinaria", icon: "ri-error-warning-line" },
  { value: "reunion_equipo", label: "Reunión de equipo", icon: "ri-group-line" },
  { value: "comunicacion_tribunal", label: "Comunicación con tribunal", icon: "ri-scales-line" },
];

export default function NuevaIntervencionPage() {
  const navigate = useNavigate();
  const [tipo, setTipo] = useState("entrevista_estudiante");
  const [estudianteId, setEstudianteId] = useState("");
  const [casoId, setCasoId] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [acuerdos, setAcuerdos] = useState("");
  const [proximosPasos, setProximosPasos] = useState("");
  const [confidencial, setConfidencial] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [students, setStudents] = useState<any[]>([]);
  const [allCases, setAllCases] = useState<any[]>([]);

  useEffect(() => {
    async function loadData() {
      try {
        const [studData, casesData] = await Promise.all([
          estudianteService.list(),
          caseService.list()
        ]);
        if (Array.isArray(studData)) setStudents(studData);
        if (Array.isArray(casesData)) setAllCases(casesData.map(mapDbCaseToFrontend));
      } catch (err: any) {
        setError("Error al cargar datos desde Azure: " + (err.response?.data?.detail || err.message));
      }
    }
    loadData();
  }, []);

  const casosDelEstudiante = estudianteId ? allCases.filter((c) => c.studentId === estudianteId && c.estado !== "cerrado") : [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      await interventionService.create({
        caso_id: casoId,
        tipo: tipo,
        descripcion: descripcion,
        fecha: new Date().toISOString().split("T")[0],
        participantes: [],
        acuerdos: acuerdos || null,
        proximos_pasos: proximosPasos || null,
        confidencial: confidencial
      });

      setGuardado(true);
      setTimeout(() => {
        setGuardado(false);
        if (casoId) {
          navigate(`/casos/${casoId}`);
        } else {
          navigate("/casos");
        }
      }, 1500);
    } catch (err: any) {
      console.error("Error creating intervention:", err);
      setError("Error al registrar intervención en Azure: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in-up max-w-2xl">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Registrar Intervención</h1>
        <p className="text-sm text-foreground-500 mt-0.5">Documenta una acción realizada en un caso de convivencia escolar</p>
      </div>

      {guardado ? (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
          <i className="ri-check-double-line text-3xl text-green-500 mb-2 block" />
          <p className="text-base font-semibold text-green-700">Intervención registrada exitosamente</p>
          <p className="text-sm text-green-600 mt-1">Redirigiendo al caso...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white border border-background-200/80 rounded-2xl p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-2">Estudiante</label>
            <select
              value={estudianteId}
              onChange={(e) => { setEstudianteId(e.target.value); setCasoId(""); }}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
              required
            >
              <option value="">Seleccionar estudiante</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre} · {s.curso}</option>
              ))}
            </select>
          </div>

          {estudianteId && casosDelEstudiante.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-foreground-700 mb-2">Caso asociado</label>
              <select
                value={casoId}
                onChange={(e) => setCasoId(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
              >
                <option value="">Sin caso específico</option>
                {casosDelEstudiante.map((c) => (
                  <option key={c.id} value={c.id}>{c.folio} · {c.titulo}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-2">Tipo de intervención</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {INTERVENCION_TIPOS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTipo(t.value)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium border transition-colors cursor-pointer ${
                    tipo === t.value
                      ? "border-primary-300 bg-primary-50 text-primary-700"
                      : "border-background-200 text-foreground-600 hover:bg-background-50"
                  }`}
                >
                  <span className="flex items-center justify-center w-4 h-4"><i className={t.icon} /></span>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-2">Descripción</label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={4}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
              placeholder="Describe la intervención realizada..."
              maxLength={500}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-2">Acuerdos</label>
            <textarea
              value={acuerdos}
              onChange={(e) => setAcuerdos(e.target.value)}
              rows={2}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
              placeholder="Acuerdos alcanzados..."
              maxLength={500}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-2">Próximos pasos</label>
            <textarea
              value={proximosPasos}
              onChange={(e) => setProximosPasos(e.target.value)}
              rows={2}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
              placeholder="Qué se hará a continuación..."
              maxLength={500}
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="relative inline-flex cursor-pointer items-center">
              <input type="checkbox" checked={confidencial} onChange={(e) => setConfidencial(e.target.checked)} className="sr-only peer" />
              <div className="w-9 h-5 bg-background-200 rounded-full peer-checked:bg-purple-500 after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
            </label>
            <span className="text-sm text-foreground-600">Registro confidencial</span>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" className="px-6 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              Guardar Intervención
            </button>
            <button type="button" onClick={() => navigate(-1)} className="px-6 py-2.5 text-sm font-medium text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap">
              Cancelar
            </button>
          </div>
        </form>
      )}
    </div>
  );
}